import type { AccountSession } from "./accountTypes";

/**
 * Local-dev auth bypass, PWA half (multi-device testing over LAN/Tailscale).
 *
 * Real sign-in is unreachable from a second device against a local dev API
 * (OAuth providers reject the network-IP `redirect_uri`, Email OTP fails
 * closed without Turnstile keys, the API fails closed to `invite_only`), so
 * this lets the app run as a scratch dev identity instead. The server half
 * lives in ato-api `src/dev_auth_bypass.ts` — same user id, same header, same
 * fail-closed posture.
 *
 * TWO gates, both required:
 *   1. `import.meta.env.DEV` — true only when served by `vite dev`, NEVER in
 *      `vite build` output. A token baked into a production build is inert.
 *   2. `VITE_DEV_AUTH_BYPASS_TOKEN` set (the per-run token
 *      `scripts/dev.mjs --bypass-auth` prints in apps/ato-api).
 *
 * While enabled, `fetchAccountSession()` resolves the canned session below
 * with NO network call (better-auth's own `/get-session` would just say
 * "no session" — the bypass is not a real cookie session), every account API
 * client carries the token header (see `withConnectionAuth` and
 * `accountAuthClient`'s fetch wrapper), and sign-out is a no-op (reload
 * re-applies the bypass; restart without the token to truly sign out).
 */

/** Request header the dev API gates on (also in its CORS allowlist). */
export const DEV_BYPASS_HEADER = "X-Ato-Dev-Bypass";

/** Must match ato-api's `DEV_BYPASS_USER_ID` exactly. */
export const DEV_BYPASS_USER_ID = "dev-local-user";

/** The per-run token, or null when the bypass is not armed from outside. */
export function devBypassToken(): string | null {
  const raw = (
    import.meta.env.VITE_DEV_AUTH_BYPASS_TOKEN as string | undefined
  )?.trim();
  return raw ? raw : null;
}

/** Whether the PWA half of the bypass is active right now. */
export function isDevBypassEnabled(): boolean {
  // import.meta.env.DEV is compile-time: production bundles can never take
  // this branch no matter what env they are served with.
  if (!import.meta.env.DEV) return false;
  return devBypassToken() !== null;
}

/** The scratch identity the app runs as while the bypass is active. */
export function devBypassSession(): AccountSession {
  return {
    user: {
      id: DEV_BYPASS_USER_ID,
      email: "dev@local.ato",
      name: "Local Dev",
      image: null,
    },
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  };
}
